// Account-detail server-side analytics — fetchers + adapters.
//
// Option 2 (2026-06-13): the account-detail page used to pull the ENTIRE
// trade_fills + closed_positions history client-side and compute ~12
// aggregates in JS. That is now done in the DB by four SECURITY INVOKER RPCs
// (parity-verified to the cent — see tasks/account-detail-server-aggregates-spec.md):
//   veritas.account_trade_analytics   → KPI primitives, daily, hourly, histogram,
//                                        volume bands, bot/manual split, magic picker
//   veritas.account_bot_breakdown     → per-magic S8 panel rows
//   veritas.account_position_analytics→ r_stats, calendar, intraday
//   veritas.account_behavior_analytics→ per_tag + mistakes
//
// The RPCs return RAW PRIMITIVES; the adapters below rebuild the EXACT shapes
// the existing presentational components consume (Kpis, DayBucket, IntradayPoint,
// RStats, TagStatsRow, MistakeRow, BotPerfRow inputs) using the same derived
// formulas as src/lib/metrics.ts / r-multiples.ts — so JS math stays authoritative
// and only the heavy reduction moved server-side.

import { veritas } from './supabase';
import type { Kpis, DayBucket, IntradayPoint, EquityPoint } from './metrics';
import type { RStats, TagStatsRow, MistakeRow } from './r-multiples';

// ── raw RPC payload types ──────────────────────────────────────────────────
export type TradeKpisRaw = {
  total_trades: number; closed_positions: number; winners: number; losers: number;
  gross_win: number; gross_loss: number; net_pnl: number; total_volume: number;
  win_streak: number; loss_streak: number;
};
export type TradeAnalytics = {
  kpis: TradeKpisRaw;
  daily: Array<{ date: string; profit: number; trades: number; wins: number; losses: number; volume: number }>;
  hourly: Array<{ hour: number; profit: number; trades: number }>;
  histogram: Array<{ lo: number; hi: number; count: number }>;
  volume_buckets: Array<{ bi: number; count: number; profit: number }>;
  source: Partial<Record<'bot' | 'manual', { count: number; profit: number; closing: number; wins: number; volume: number }>>;
  magics: Array<{ magic: number; count: number; pnl: number }>;
};
export type BotBreakdownRow = {
  magic: number; trades: number; net_pnl: number; wins: number;
  gross_win: number; gross_loss: number; best: number; worst: number;
  volume: number; last_close: string | null; realized_dd_abs: number;
  base_n: number; base_sum: number; trail_n: number; trail_sum: number;
};
export type PositionAnalytics = {
  r_stats: { count: number; withR: number; avgR: number | null; expectancyR: number | null;
             winRate: number | null; bestR: number | null; worstR: number | null };
  calendar: Array<{ date: string; profit: number; trades: number; wins: number; losses: number }>;
  intraday: Array<{ hour: number; avgCumulative: number; days: number }>;
};
export type BehaviorAnalytics = {
  per_tag: Array<{ tagId: string; tagName: string; category: string | null; color: string;
                   count: number; winRate: number; totalPnL: number; expectancyR: number | null }>;
  mistakes: Array<{ tagName: string; taggedCount: number; taggedAvgR: number | null; taggedAvgPnL: number;
                    untaggedAvgR: number | null; untaggedAvgPnL: number; rDelta: number | null; pnlDelta: number }>;
};

const n = (v: unknown): number => Number(v ?? 0);
const nOrNull = (v: unknown): number | null => (v == null ? null : Number(v));

// ── fetchers ────────────────────────────────────────────────────────────────
export async function fetchTradeAnalytics(login: number, cred: string | null, magic: number | null) {
  const { data, error } = await veritas.rpc('account_trade_analytics', {
    p_account_login: login, p_credential_id: cred, p_magic: magic,
  });
  if (error) throw error;
  return data as TradeAnalytics;
}
export async function fetchBotBreakdown(login: number, cred: string | null) {
  const { data, error } = await veritas.rpc('account_bot_breakdown', {
    p_account_login: login, p_credential_id: cred,
  });
  if (error) throw error;
  return (data ?? []) as BotBreakdownRow[];
}
export async function fetchPositionAnalytics(login: number, cred: string | null, magic: number | null, source: string) {
  const { data, error } = await veritas.rpc('account_position_analytics', {
    p_account_login: login, p_credential_id: cred, p_magic: magic, p_source: source,
  });
  if (error) throw error;
  return data as PositionAnalytics;
}
export async function fetchBehaviorAnalytics(login: number, cred: string | null, magic: number | null, source: string) {
  const { data, error } = await veritas.rpc('account_behavior_analytics', {
    p_account_login: login, p_credential_id: cred, p_magic: magic, p_source: source,
  });
  if (error) throw error;
  return data as BehaviorAnalytics;
}

// ── adapters (rebuild metrics.ts / r-multiples.ts shapes) ────────────────────
export const EMPTY_KPIS: Kpis = {
  netPnL: 0, returnPct: 0, initialBalance: 0, finalBalance: 0,
  totalTrades: 0, closedPositions: 0, winners: 0, losers: 0, winRate: 0,
  profitFactor: 0, avgWin: 0, avgLoss: 0, expectancy: 0,
  totalVolume: 0, maxDdAbs: 0, maxDdPct: 0,
  longestWinStreak: 0, longestLossStreak: 0,
};

// Mirrors computeKpis(): trade primitives come from the RPC; the equity-derived
// parts (initial/final/returnPct/maxDd) are computed here from the snapshot series.
export function kpisFromAnalytics(equity: EquityPoint[], k: TradeKpisRaw): Kpis {
  if (equity.length === 0 || k.total_trades === 0) return EMPTY_KPIS;
  const initial = equity[0].balance;
  const final = equity[equity.length - 1].balance;
  const winners = k.winners, losers = k.losers;
  const grossWin = n(k.gross_win), grossLoss = n(k.gross_loss);
  const closing = k.closed_positions;
  const winRate = closing > 0 ? (winners / closing) * 100 : 0;
  const profitFactor = grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0;
  const avgWin = winners ? grossWin / winners : 0;
  const avgLoss = losers ? -grossLoss / losers : 0;
  const expectancy = (winRate / 100) * avgWin + (1 - winRate / 100) * avgLoss;
  let peak = -Infinity, maxDdAbs = 0, maxDdPct = 0;
  for (const p of equity) {
    if (p.balance > peak) peak = p.balance;
    const ddAbs = p.balance - peak;
    const ddPct = peak > 0 ? (ddAbs / peak) * 100 : 0;
    if (ddAbs < maxDdAbs) maxDdAbs = ddAbs;
    if (ddPct < maxDdPct) maxDdPct = ddPct;
  }
  return {
    netPnL: n(k.net_pnl),
    returnPct: initial > 0 ? ((final - initial) / initial) * 100 : 0,
    initialBalance: initial, finalBalance: final,
    totalTrades: k.total_trades, closedPositions: closing,
    winners, losers, winRate, profitFactor, avgWin, avgLoss, expectancy,
    totalVolume: n(k.total_volume),
    maxDdAbs: Math.abs(maxDdAbs), maxDdPct: Math.abs(maxDdPct),
    longestWinStreak: k.win_streak, longestLossStreak: k.loss_streak,
  };
}

export function dailyFromAnalytics(a: TradeAnalytics) {
  return a.daily.map((d) => ({
    date: d.date, profit: n(d.profit), trades: d.trades,
    wins: d.wins, losses: d.losses, volume: n(d.volume),
  }));
}
export function hourlyFromAnalytics(a: TradeAnalytics) {
  return a.hourly.map((h) => ({ hour: h.hour, profit: n(h.profit), trades: h.trades }));
}
export function histogramFromAnalytics(a: TradeAnalytics) {
  return a.histogram.map((b) => {
    const lo = n(b.lo), hi = n(b.hi);
    return { label: `${lo.toFixed(0)}…${hi.toFixed(0)}`, midpoint: (lo + hi) / 2, count: b.count, positive: (lo + hi) / 2 >= 0 };
  });
}

const VOLUME_BAND_LABELS = [
  '0.01', '0.02-0.05', '0.06-0.10', '0.11-0.25', '0.26-0.50', '0.51-1.00',
  '1.01-2.50', '2.51-5.00', '5.01-10.00', '10.01-25.00', '> 25.00',
];
export function volumeBucketsFromAnalytics(a: TradeAnalytics) {
  const total = a.kpis.total_trades || 1;
  return a.volume_buckets.map((b) => ({
    label: VOLUME_BAND_LABELS[b.bi] ?? String(b.bi),
    count: b.count, profit: n(b.profit), countShare: b.count / total,
  }));
}

export type SourceSeg = { count: number; profit: number; winRate: number; volume: number };
export function sourceFromAnalytics(a: TradeAnalytics): { bot: SourceSeg; manual: SourceSeg } {
  const seg = (s?: { count: number; profit: number; closing: number; wins: number; volume: number }): SourceSeg => ({
    count: s?.count ?? 0,
    profit: n(s?.profit),
    winRate: s && s.closing > 0 ? (s.wins / s.closing) * 100 : 0,
    volume: n(s?.volume),
  });
  return { bot: seg(a.source.bot), manual: seg(a.source.manual) };
}

export const EMPTY_RSTATS: RStats = {
  count: 0, withR: 0, avgR: null, expectancyR: null, winRate: null, bestR: null, worstR: null,
};
export function rStatsFromAnalytics(p: PositionAnalytics): RStats {
  const r = p.r_stats;
  return {
    count: r.count, withR: r.withR,
    avgR: nOrNull(r.avgR), expectancyR: nOrNull(r.expectancyR), winRate: nOrNull(r.winRate),
    bestR: nOrNull(r.bestR), worstR: nOrNull(r.worstR),
  };
}

export function calendarMapFromAnalytics(p: PositionAnalytics): Map<string, DayBucket> {
  const m = new Map<string, DayBucket>();
  for (const d of p.calendar) {
    m.set(d.date, { profit: n(d.profit), trades: d.trades, wins: d.wins, losses: d.losses });
  }
  return m;
}

const ZERO_INTRADAY: IntradayPoint[] = Array.from({ length: 24 }, (_, h) => ({ hour: h, avgCumulative: 0, days: 0 }));
export function intradayFromAnalytics(p: PositionAnalytics | null): IntradayPoint[] {
  if (!p || !p.intraday?.length) return ZERO_INTRADAY;
  return p.intraday.map((i) => ({ hour: i.hour, avgCumulative: n(i.avgCumulative), days: i.days }));
}

export function perTagFromBehavior(b: BehaviorAnalytics): TagStatsRow[] {
  return b.per_tag
    .filter((t) => t.count > 0)
    .map((t) => ({
      tagId: t.tagId, tagName: t.tagName, category: t.category, color: t.color,
      count: t.count, winRate: n(t.winRate), totalPnL: n(t.totalPnL), expectancyR: nOrNull(t.expectancyR),
    }));
}
export function mistakesFromBehavior(b: BehaviorAnalytics): MistakeRow[] {
  return b.mistakes
    .filter((m) => m.taggedCount > 0)
    .map((m) => ({
      tagName: m.tagName, taggedCount: m.taggedCount,
      taggedAvgR: nOrNull(m.taggedAvgR), taggedAvgPnL: n(m.taggedAvgPnL),
      untaggedAvgR: nOrNull(m.untaggedAvgR), untaggedAvgPnL: n(m.untaggedAvgPnL),
      rDelta: nOrNull(m.rDelta), pnlDelta: n(m.pnlDelta),
    }));
}
